/**
 * PWA-Update-Mechanik. Ohne diese sehen installierte Nutzer neue App-Versionen
 * nicht (der Service Worker cached die App-Shell). Ablauf:
 *  1. main.tsx registriert den SW über registerWithUpdates().
 *  2. Findet der Browser einen neuen, WARTENDEN SW, setzen wir updateReady=true.
 *  3. Das <UpdateBanner/> erscheint; tippt der Nutzer „Neu laden", schicken wir
 *     dem wartenden SW SKIP_WAITING und laden bei controllerchange neu.
 */
import { signal } from "@preact/signals";

export const updateReady = signal<boolean>(false);
let waiting: ServiceWorker | null = null;
let reloading = false;
// Reload NUR, wenn der Nutzer bewusst „Neu laden" getippt hat — sonst würde der
// Erstbesuch (SW übernimmt via clients.claim) fälschlich neu laden (Draft-Verlust).
let userTriggered = false;

function watch(reg: ServiceWorkerRegistration): void {
  // Bereits ein wartender SW vorhanden (z. B. zwischen Sitzungen aktualisiert).
  if (reg.waiting && navigator.serviceWorker.controller) {
    waiting = reg.waiting;
    updateReady.value = true;
  }
  reg.addEventListener("updatefound", () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      // „installed" + es gibt schon einen Controller = echtes Update (kein Erstbesuch).
      if (installing.state === "installed" && navigator.serviceWorker.controller) {
        waiting = reg.waiting || installing;
        updateReady.value = true;
      }
    });
  });
}

export function registerWithUpdates(): void {
  if (!("serviceWorker" in navigator)) return;
  addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        watch(reg);
        // Periodisch nach Updates schauen (App bleibt als PWA lange offen).
        setInterval(() => void reg.update().catch(() => {}), 60 * 60 * 1000);
      })
      .catch(() => {
        /* SW optional — App funktioniert auch ohne. */
      });
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!userTriggered || reloading) return;
      reloading = true;
      location.reload();
    });
  });
}

export function applyUpdate(): void {
  updateReady.value = false;
  userTriggered = true;
  if (waiting) waiting.postMessage({ type: "SKIP_WAITING" });
  else location.reload();
}

export function UpdateBanner() {
  if (!updateReady.value) return null;
  return (
    <div class="sw-update" role="status" aria-live="polite">
      <span>Eine neue Version ist verfügbar.</span>
      <button type="button" class="sw-update-btn" onClick={applyUpdate}>
        Neu laden
      </button>
    </div>
  );
}
